import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as purchaseApi from '../api/purchase.api'

const initialState = {
  isSubmitting: false,
  isLoading: false,
  error: null,
  purchases: [],
}

export const usePurchaseStore = create((set, get) => ({
  ...initialState,

  clearError: () =>
    set({
      error: null,
    }),

  fetchPurchasePersons: async () => {
    if (get().isLoading) return

    set({
      isLoading: true,
      error: null,
    })

    try {
      const result = await purchaseApi.getPurchasePersons()
      const purchases = Array.isArray(result.data) ? result.data : []

      set({
        purchases,
        isLoading: false,
        error: null,
      })

      return purchases
    } catch (error) {
      const message = error.message || 'Failed to fetch Purchase persons'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  createPurchasePerson: async (payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await purchaseApi.createPurchase(payload)

      toast.success(result.message || 'Purchase person created successfully')

      set((state) => {
        const newPurchase = result.data

        return {
          isSubmitting: false,
          error: null,
          purchases: newPurchase
            ? [
                newPurchase,
                ...state.purchases.filter(
                  (person) =>
                    String(person._id || person.id) !==
                    String(newPurchase._id || newPurchase.id),
                ),
              ]
            : state.purchases,
        }
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to create Purchase person'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updatePurchasePerson: async (id, payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await purchaseApi.updatePurchase(id, payload)

      toast.success(result.message || 'Purchase person updated successfully')

      set((state) => {
        const updated = result.data

        return {
          isSubmitting: false,
          error: null,
          purchases: state.purchases.map((person) =>
            String(person._id || person.id) === String(id)
              ? { ...person, ...updated }
              : person,
          ),
        }
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to update Purchase person'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  deletePurchasePerson: async (id) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await purchaseApi.deletePurchase(id)

      set((state) => ({
        isSubmitting: false,
        error: null,
        purchases: state.purchases.filter(
          (person) => String(person._id || person.id) !== String(id),
        ),
      }))

      return result
    } catch (error) {
      const message = error.message || 'Failed to delete Purchase person'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },
}))
